import { addActivityLog } from "../logs/actions";
import { EMPLOYEES, IDX_EMPLOYEES, USERS } from "../../constants/collections";
import { SCHOOL_ADMIN } from "../../constants/userAccounts";
import { addSchoolToEmployee, searchEmployee } from "../../helpers/employees";
import { addEmployeeToSchool } from "../../helpers/schools";
import map from "lodash/map";
import includes from "lodash/includes";

export const CREATE_EMPLOYEE_START = "CREATE_EMPLOYEE_START",
  CREATE_EMPLOYEE_SUCCESS = "CREATE_EMPLOYEE_SUCCESS",
  CREATE_EMPLOYEE_FAIL = "CREATE_EMPLOYEE_FAIL",
  QUERY_NEW_EMPLOYEE_START = "QUERY_NEW_EMPLOYEE_START",
  QUERY_NEW_EMPLOYEE_SUCCESS = "QUERY_NEW_EMPLOYEE_SUCCESS",
  QUERY_NEW_EMPLOYEE_FAIL = "QUERY_NEW_EMPLOYEE_FAIL",
  ADD_EXISTING_EMPLOYEES = "ADD_EXISTING_EMPLOYEES",
  SOFT_DELETE_EMPLOYEE_START = "SOFT_DELETE_EMPLOYEE_START",
  SOFT_DELETE_EMPLOYEE_SUCCESS = "SOFT_DELETE_EMPLOYEE_SUCCESS",
  SOFT_DELETE_EMPLOYEE_FAIL = "SOFT_DELETE_EMPLOYEE_FAIL";

const createNewEmployeeStart = (newEmployee) => ({
  type: CREATE_EMPLOYEE_START,
  newEmployee,
});

const createNewEmployeeSuccess = (newEmployeeId) => ({
  type: CREATE_EMPLOYEE_SUCCESS,
  newEmployeeId,
});

const createNewEmployeeFail = (error) => ({
  type: CREATE_EMPLOYEE_FAIL,
  error: error,
});

export const createNewEmployee =
  ({ employee, school, sy, employeeId, edit }) =>
  async (dispatch, getState, { getFirebase, getFirestore }) => {
    dispatch(
      createNewEmployeeStart(
        [
          employee.firstName,
          employee.middleName,
          employee.lastName,
          employee.extName,
        ].join(" "),
      ),
    );
    try {
      const db = getFirestore();
      const newEmployeeRef = !edit
        ? db.collection(EMPLOYEES).doc()
        : db.collection(EMPLOYEES).doc(employeeId);
      if (!edit) {
        await newEmployeeRef.set({
          ...employee,
          createdOn: db.FieldValue.serverTimestamp(),
          lastUpdated: db.FieldValue.serverTimestamp(),
          deleted: false,
        });
      } else {
        await newEmployeeRef.set(
          {
            ...employee,
            lastUpdated: db.FieldValue.serverTimestamp(),
          },
          { merge: true },
        );
      }

      dispatch(createNewEmployeeSuccess(newEmployeeRef.id));

      if (!edit && school) {
        // Add document to subcollection "schools" if schoolID is present
        await addSchoolToEmployee(newEmployeeRef.id, school, sy);
        await addEmployeeToSchool(
          school,
          newEmployeeRef.id,
          sy,
          employee.teaching,
        );

        // Init school_role
        await db
          .collection(EMPLOYEES)
          .doc(newEmployeeRef.id)
          .collection("school_role")
          .doc(sy)
          .set(
            {
              role: employee.teaching ? "teacher" : "unassigned",
              schoolId: school,
            },
            { merge: true },
          );
      }

      const newEmployeeDoc = await newEmployeeRef.get();
      const newEmployee = { ...newEmployeeDoc.data(), id: newEmployeeDoc.id };
      dispatch(
        addActivityLog({
          doc: newEmployee,
          action: !edit ? "createNewEmployee" : "updateEmployee",
          actor: !edit ? employee.createdBy : employee.updatedBy,
        }),
      );
    } catch (error) {
      dispatch(createNewEmployeeFail(error));
    }
  };

const queryNewEmployeeStart = () => ({
  type: QUERY_NEW_EMPLOYEE_START,
});

const queryNewEmployeeSuccess = () => ({
  type: QUERY_NEW_EMPLOYEE_SUCCESS,
});

const queryNewEmployeeFail = (error) => ({
  type: QUERY_NEW_EMPLOYEE_FAIL,
  error,
});

export const queryNewEmployee =
  ({ employee, employeeId, edit }) =>
  async (dispatch, getState, { getFirebase, getFirestore }) => {
    dispatch(queryNewEmployeeStart());
    try {
      const db = getFirestore();
      const employeeRef = db.collection(IDX_EMPLOYEES);
      const employeeNameQuery = employeeRef.where(
        "keywords",
        "array-contains",
        `${employee.firstName.toLowerCase()} ${employee.middleName.toLowerCase()} ${employee.lastName.toLowerCase()}&all&all`,
      );
      const employeeNameSnap = await employeeNameQuery.get();

      if (!edit && !employeeNameSnap.empty) {
        throw new Error("query-error-fullname");
      } else if (edit && !employeeNameSnap.empty) {
        const docIds = map(employeeNameSnap.docs, "id");
        if (!includes(docIds, employeeId)) {
          throw new Error("query-error-fullname");
        }
      }
      // if (!employeeNameSnap.empty) {
      // 	throw new Error('query-error-fullname');
      // }
      dispatch(queryNewEmployeeSuccess());
    } catch (error) {
      console.log(error);
      let errorBody = error;
      if (error.message === "query-error-fullname") {
        errorBody = {
          message: "That employee's name is already taken.",
          field: "fullname",
        };
      }
      dispatch(queryNewEmployeeFail(errorBody));
    }
  };

export const addExistingEmployees =
  ({ teaching, employees, schoolId, sy, method }) =>
  async (dispatch, getState, { getFirestore }) => {
    try {
      const db = getFirestore();
      async function processAdding(employeeId) {
        // Add document to subcollection "schools" if schoolID is present
        await addSchoolToEmployee(employeeId, schoolId, sy);
        await addEmployeeToSchool(schoolId, employeeId, sy, teaching);

        // Init school_role
        await db
          .collection(EMPLOYEES)
          .doc(employeeId)
          .collection("school_role")
          .doc(sy)
          .set(
            {
              role: teaching ? "teacher" : "unassigned",
              schoolId: schoolId,
              deleted: false,
            },
            { merge: true },
          );
      }
      if (method === "fromSY") {
        // employees here is the SY
        const querySnap = await searchEmployee(
          teaching,
          employees,
          schoolId,
          "",
        );
        if (!querySnap.empty) {
          querySnap.forEach((doc) => processAdding(doc.id));
        }
      } else {
        // employees here is array of value-label collection
        employees.forEach((employees) => {
          processAdding(employees.value);
        });
      }
      dispatch({ type: ADD_EXISTING_EMPLOYEES });
    } catch (error) {
      throw error;
    }
  };

export const softDeleteEmployee =
  ({ employeeId, sy, schoolId }) =>
  async (dispatch, getState, { getFirestore, getFirebase }) => {
    dispatch({ type: SOFT_DELETE_EMPLOYEE_START });
    try {
      const db = getFirestore();
      const firebase = getFirebase();
      const auth = firebase.auth().currentUser;
      const deletedBy = auth ? auth.uid : "system";

      // 1. Soft Delete Main Doc (SKIPPED for Contextual Delete)
      // await db.collection(EMPLOYEES).doc(employeeId).update({
      //   deleted: true,
      //   lastUpdated: db.FieldValue.serverTimestamp(),
      // });

      // 2. Soft Delete School Role (Trigger Logic)
      await db
        .collection(EMPLOYEES)
        .doc(employeeId)
        .collection("school_role")
        .doc(sy)
        .set(
          {
            deleted: true,
            lastUpdated: db.FieldValue.serverTimestamp(),
          },
          { merge: true },
        );

      // 3. Remove School ID from schools/{sy} array
      await db
        .collection(EMPLOYEES)
        .doc(employeeId)
        .collection("schools")
        .doc(sy)
        .update({
          schools: firebase.firestore.FieldValue.arrayRemove(schoolId),
        });

      // 4. Role Cleanup: Remove SCHOOL_ADMIN role from global user profile
      const userAccountsRef = db
        .collection(EMPLOYEES)
        .doc(employeeId)
        .collection("user_accounts");

      const userAccountsSnap = await userAccountsRef.get();

      if (!userAccountsSnap.empty) {
        const batch = db.batch();
        let batchHasOps = false;

        userAccountsSnap.docs.forEach((doc) => {
          const data = doc.data();
          if (data.uid) {
            const userRef = db.collection(USERS).doc(data.uid);
            batch.update(userRef, {
              roles: firebase.firestore.FieldValue.arrayRemove(SCHOOL_ADMIN),
            });
            batchHasOps = true;
          }
        });

        if (batchHasOps) {
          await batch.commit();
        }
      }

      dispatch(
        addActivityLog({
          doc: { id: employeeId },
          action: "softDeleteEmployee",
          actor: deletedBy,
        }),
      );

      dispatch({ type: SOFT_DELETE_EMPLOYEE_SUCCESS, employeeId });
    } catch (error) {
      dispatch({ type: SOFT_DELETE_EMPLOYEE_FAIL, error });
      throw error;
    }
  };
